import { Helmet } from 'react-helmet-async';
import React from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  agentCardUrl?: string;
  ogImage?: string | null;
  ogType?: 'website' | 'article' | 'video.other';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  noindex?: boolean;
  twitterCard?: 'summary' | 'summary_large_image' | 'player';
  twitterSite?: string;
  twitterCreator?: string;
}

const BASE_URL = 'https://fivedollarworkday.com';

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  agentCardUrl,
  ogImage,
  ogType = 'website',
  article,
  noindex = false,
  twitterCard = 'summary_large_image',
  twitterSite = '@FiveWorkday',
  twitterCreator = '@JeremyMearsX',
}) => {
  const fullTitle = title.includes('Five Dollar')
    ? title
    : `${title} | Five Dollar Workday`;

  const url = canonical || `${BASE_URL}${window.location.pathname}`;

  let imageToUse = '/og-image.png';

  if (ogImage && ogImage !== '/og-image.png') {
    imageToUse = ogImage;
  } else if (article?.section) {
    const section = article.section.toLowerCase();

    if (section.includes('technical')) {
      imageToUse = '/og/engineering.png';
    } else if (section.includes('gateway')) {
      imageToUse = '/og/public.png';
    } else if (section.includes('product')) {
      imageToUse = '/og/product.png';
    } else if (section.includes('founder')) {
      imageToUse = '/og/philosophy.png';
    }
  }

  const fullImageUrl = imageToUse.startsWith('http')
    ? imageToUse
    : `${BASE_URL}${imageToUse.startsWith('/') ? '' : '/'}${imageToUse}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>

      <meta name="description" content={description} />

      <link rel="canonical" href={url} />

      {/* Agent Card discovery.
          This deliberately points directly to the entity's canonical
          machine-readable Agent Card. */}
      {agentCardUrl && (
        <>
          <meta
            name="agent-card"
            content={agentCardUrl}
          />

          <link
            rel="alternate"
            type="application/json"
            href={agentCardUrl}
          />
        </>
      )}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Five Dollar Workday" />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      <meta
        name="twitter:image:alt"
        content={`${title} - Five Dollar Workday`}
      />

      {article && (
        <>
          {article.publishedTime && (
            <meta
              property="article:published_time"
              content={article.publishedTime}
            />
          )}

          {article.modifiedTime && (
            <meta
              property="article:modified_time"
              content={article.modifiedTime}
            />
          )}

          {article.author && (
            <meta
              property="article:author"
              content={article.author}
            />
          )}

          {article.section && (
            <meta
              property="article:section"
              content={article.section}
            />
          )}

          {article.tags?.map((tag) => (
            <meta
              key={tag}
              property="article:tag"
              content={tag}
            />
          ))}
        </>
      )}

      {noindex && (
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      )}

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Five Dollar Workday',
          url: BASE_URL,
          logo: `${BASE_URL}/favicon.svg`,
          sameAs: [
            'https://x.com/FiveWorkday',
            'https://x.com/JeremyMearsX',
          ],
          description:
            'Five Dollar Workday is an AI-native service business. It sells completed, governed outcomes directly to customers — an AI-native process performs the work, a human reviews every decision that needs one, and every engagement produces a permanent, verifiable record of how it was done.',
          contactPoint: {
            '@type': 'ContactPoint',
            url: `${BASE_URL}/registry`,
            contactType: 'customer support',
          },
        })}
      </script>
    </Helmet>
  );
};